import {
  CheckoutState,
  RelayExecutionStatus,
  getDirectExecutionByTxHash,
} from '@funkit/api-base'
import {
  isDirectExecutionNotFound,
  isTerminalRelayStatus,
  mapRelayExecutionStatusToCheckoutState,
} from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'
import { useRef } from 'react'

import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'

export const WITHDRAWAL_STATUS_QUERY_KEY = 'withdrawalStatus'

/** Web polls the same record at this cadence. */
const POLL_INTERVAL_MS = 1_500

// How many times the backend may say "no such record" before we stop asking. Web
// polls indefinitely, but its caller seeds the query with a record it already
// has; here the record may never be written, and this screen can sit open.
const MAX_FETCHES_WITHOUT_RECORD = 10

/**
 * Whether there is still anything to learn by asking again. Split out because
 * the "record never appeared" bound is the part worth testing directly — driving
 * it through the query would mean waiting out real poll intervals.
 *
 * Only answered-and-absent counts against the bound: a network or 5xx failure
 * says nothing about the record, so giving up on one would abandon a withdrawal
 * we could still have followed.
 */
export function shouldKeepPollingStatus({
  status,
  hasRecord,
  notFoundFetches,
}: {
  status: RelayExecutionStatus | undefined
  hasRecord: boolean
  notFoundFetches: number
}): boolean {
  if (isTerminalRelayStatus(status)) {
    return false
  }
  return hasRecord || notFoundFetches < MAX_FETCHES_WITHOUT_RECORD
}

export interface WithdrawalStatusResult {
  status: RelayExecutionStatus | undefined
  /** Confirmed on-chain. Only `SUCCESS` — not merely "stopped moving". */
  isCompleted: boolean
  /** `FAILURE` or `REFUND`: the transfer did not reach the recipient. */
  isFailed: boolean
  /** Funds came back to the sender, so there is nothing to chase. */
  isRefunded: boolean
}

/**
 * Follow a submitted withdrawal to a terminal state.
 *
 * The record is written best-effort by the pipeline, so a missing one is not a
 * failure — the transfer is already on-chain. It just means we cannot report
 * progress, and polling stops rather than hammering the API forever. An
 * explicit terminal failure is different, and is reported as one.
 */
export function useWithdrawalStatus(
  txHash: string | undefined,
): WithdrawalStatusResult {
  const { apiKey } = useFunkitIdentity()
  // Counted here because react-query's own `errorUpdateCount` cannot tell "no
  // such record" from "could not ask".
  const notFoundFetches = useRef(0)

  // The bound is per hash, not per mount: a caller that advances through the
  // steps of a route would otherwise hand the arrival a budget an earlier step
  // had already spent, and never poll it.
  const countedFor = useRef(txHash)
  if (countedFor.current !== txHash) {
    countedFor.current = txHash
    notFoundFetches.current = 0
  }

  const query = useQuery({
    queryKey: [WITHDRAWAL_STATUS_QUERY_KEY, apiKey, txHash],
    queryFn: async () => {
      try {
        const record = await getDirectExecutionByTxHash({
          txHash: txHash as `0x${string}`,
          apiKey,
          logger,
        })
        notFoundFetches.current = 0
        return record
      } catch (error) {
        if (isDirectExecutionNotFound(error)) {
          notFoundFetches.current += 1
        }
        throw error
      }
    },
    enabled: !!apiKey && !!txHash,
    refetchInterval: (query) => {
      const hasRecord = query.state.data !== undefined
      const keepPolling = shouldKeepPollingStatus({
        status: query.state.data?.listenerInfo?.status,
        hasRecord,
        notFoundFetches: notFoundFetches.current,
      })
      if (keepPolling) {
        return POLL_INTERVAL_MS
      }
      if (!hasRecord) {
        logger.warn('connect-rn:crypto-withdrawal:status_record_never_appeared')
      }
      return false
    },
    refetchOnWindowFocus: false,
    retry: 3,
  })

  const status = query.data?.listenerInfo?.status
  // Same mapping web reads through `useCheckoutDirectExecutionHistory`, so both
  // platforms agree on what a status means.
  const state = status
    ? mapRelayExecutionStatusToCheckoutState(status)
    : undefined
  return {
    status,
    isCompleted: state === CheckoutState.COMPLETED,
    isFailed: state === CheckoutState.CHECKOUT_ERROR,
    isRefunded: status === RelayExecutionStatus.REFUND,
  }
}
